import React, { useState } from 'react';

import { faArrowTurnDownLeft, faXmark } from '@fortawesome/pro-regular-svg-icons';

import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Button } from 'components/ui/button';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { OTHER, getAnswer, replaceAt } from './selection';
import { QuestionOptions } from './options';

import type { AssistantQuestion } from './types';
import type { Selection } from './selection';

interface Props {
  busy: boolean;
  questions: AssistantQuestion[];
  onDismiss: () => void;
  onSubmit: (answers: (string | null)[]) => void;
}

// One question at a time, answered or passed over, until there are none left. What becomes of the
// answers is the caller's business.
export function QuestionCard({ busy, questions, onDismiss, onSubmit }: Props) {
  const [step, setStep] = useState(0);
  const [selections, setSelections] = useState<Selection[]>(() => questions.map(() => null));
  const [others, setOthers] = useState<string[]>(() => questions.map(() => ''));

  const question = questions[step];
  const selection = selections[step];
  const lastStep = step === questions.length - 1;
  const answer = getAnswer(question, selection, others[step]);

  function choose(value: Selection) {
    setSelections(selections => replaceAt(selections, step, value));
  }

  function changeOther(text: string) {
    setOthers(others => replaceAt(others, step, text));
    choose(OTHER);
  }

  function submit(skipCurrent: boolean) {
    onSubmit(questions.map((question, index) => (
      skipCurrent && index === step ? null : getAnswer(question, selections[index], others[index])
    )));
  }

  // A skip discards whatever was picked for this question - it has to come back unanswered however
  // the card is eventually spent. Skipping the last question is also the only way a skip resolves
  // anything: everywhere else it just moves on, and a later submit carries the blank.
  function skip() {
    if (lastStep) {
      submit(true);
    } else {
      choose(null);
      setStep(step + 1);
    }
  }

  function advance() {
    if (lastStep) {
      submit(false);
    } else {
      setStep(step + 1);
    }
  }

  function advanceFromOther() {
    if (answer) {
      advance();
    }
  }

  return (
    <Card>
      <Card.Body className="d-flex flex-column gap-2">
        <div className="d-flex align-items-start gap-2">
          {questions.length > 1 && (
            <Badge appearance="muted" className="flex-shrink-0" variant="secondary">
              {step + 1}/{questions.length}
            </Badge>
          )}
          <span className="fw-semibold">{question.question}</span>
          <Button
            aria-label="Dismiss questions"
            className="ms-auto flex-shrink-0"
            disabled={busy}
            icon={faXmark}
            size="sm"
            variant="ghost"
            onClick={onDismiss}
          />
        </div>

        <QuestionOptions
          disabled={busy}
          options={question.options}
          other={others[step]}
          selection={selection}
          onChoose={choose}
          onEnter={advanceFromOther}
          onOtherChange={changeOther}
        />

        <div className="d-flex align-items-center gap-2">
          {step > 0 && (
            <Button disabled={busy} label="Back" size="sm" variant="outline" onClick={() => setStep(step - 1)} />
          )}
          <Button className="ms-auto" disabled={busy} label="Skip" size="sm" variant="outline" onClick={skip} />
          <Button
            disabled={busy || !answer}
            iconEnd={faArrowTurnDownLeft}
            label={lastStep ? 'Submit' : 'Next'}
            size="sm"
            onClick={advance}
          />
        </div>
      </Card.Body>
    </Card>
  );
}
